import React from "react";
import StaticPage from "./StaticPage";

export default function EvidenceBase() {
  return (
    <StaticPage
      eyebrow="Clinical"
      title="Evidence Base"
      updated="February 2026"
      testId="page-evidence"
    >
      <p>
        SenseWise is built directly on two frameworks that are widely used in occupational-therapy
        clinical practice and school-based OT service delivery. This page describes the models we
        follow and the boundaries of the tool.
      </p>

      <h2>1. Ayres Sensory Integration<sup>®</sup></h2>
      <p>Ayres Sensory Integration (ASI) is the framework introduced by A. Jean Ayres, PhD, OTR, in the
        1970s and formalized in the modern &quot;ASI Fidelity Measure&quot; and clinical texts. ASI
        emphasizes the role of proprioceptive, vestibular, and tactile input in helping the nervous
        system organize and produce adaptive responses.</p>
      <p>The eight sensory-system boxes in SenseWise — <em>proprioceptive, vestibular, tactile,
        visual, auditory, interoceptive, oral/gustatory,</em> and <em>olfactory</em> — reflect the sensory
        channels ASI-informed OTs assess and target in intervention.</p>
      <ul>
        <li>Ayres, A. J. (1972). <em>Sensory Integration and Learning Disorders.</em> Western
          Psychological Services.</li>
        <li>Bundy, A. C., Lane, S. J., &amp; Murray, E. A. (2020). <em>Sensory Integration: Theory and
          Practice</em> (3rd ed.). F. A. Davis.</li>
        <li>Schaaf, R. C., &amp; Mailloux, Z. (2015). <em>Clinician&apos;s Guide for Implementing Ayres
          Sensory Integration.</em> AOTA Press.</li>
      </ul>

      <h2>2. Dunn&apos;s Sensory Processing Model</h2>
      <p>Winnie Dunn&apos;s Model of Sensory Processing describes how neurological thresholds interact
        with behavioural responses to produce four quadrants of sensory processing — <strong>sensation
        seeking, sensation avoiding, sensory sensitivity,</strong> and <strong>low registration</strong>.
        SenseWise&apos;s therapist workflow lets OTs mark each sensory system as <em>seek, avoid,</em> or
        <em>neutral</em>, and this profile drives which bubbles are recommended when curating a
        student&apos;s set.</p>
      <ul>
        <li>Dunn, W. (1997). The impact of sensory processing abilities on the daily lives of young
          children and their families. <em>Infants and Young Children, 9</em>(4), 23–35.</li>
        <li>Dunn, W. (2014). <em>Sensory Profile 2 Manual.</em> Pearson.</li>
      </ul>

      <h2>3. Why feelings + sensory systems together?</h2>
      <p>Emerging OT and mental-health literature emphasizes co-regulation of emotion and sensory
        input. SenseWise&apos;s two entry points — the four core feelings (anger, fear, overwhelm,
        sadness) and the eight sensory boxes — let a student begin where they have language, and
        arrive at the same underlying interventions.</p>
      <ul>
        <li>Miller, L. J., Nielsen, D. M., &amp; Schoen, S. A. (2012). Attention deficit hyperactivity
          disorder and sensory modulation disorder: A comparison of behavior and physiology.
          <em>Research in Developmental Disabilities, 33</em>(3), 804–818.</li>
        <li>Kuypers, L. M. (2011). <em>The Zones of Regulation.</em> Think Social Publishing.
          <em>(Cited for context; SenseWise is an independent tool and is not affiliated with or endorsed
          by The Zones of Regulation.)</em></li>
      </ul>

      <h2>4. Intended use &amp; boundaries</h2>
      <p><strong>SenseWise is an educational and clinical-support tool, not a medical device.</strong>
        It is designed to (a) help students self-select regulatory strategies they and their care team
        have vetted, and (b) give therapists a visible pattern of which strategies students actually
        reach for, when.</p>
      <p>SenseWise <strong>does not</strong>:</p>
      <ul>
        <li>Provide medical or psychiatric diagnosis.</li>
        <li>Replace a licensed occupational, mental-health, or medical provider.</li>
        <li>Provide crisis care. In a mental-health emergency, dial <strong>988</strong> in the U.S. or
          your local emergency number.</li>
      </ul>

      <h2>5. Continuous improvement</h2>
      <p>Our intervention library is reviewed against the ASI framework and updated on an ongoing
        basis. Suggestions from practitioners are welcome:
        <a href="mailto:Hello@occuthera.com"> Hello@occuthera.com</a>.</p>
    </StaticPage>
  );
}
